import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow, Background, Controls, useNodesState, useEdgesState, addEdge, BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Loader2, UserCheck, Cross, Linkedin, Instagram, Music2, List, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { MemberNode } from "../components/MemberNode";
import { BottomSheet } from "../components/BottomSheet";
import { MediaUploader } from "../components/MediaUploader";
import { Avatar } from "./Hangouts";

const nodeTypes = { member: MemberNode };
const emptyDeceased = {
  name: "", photo: "", occupation: "", bio: "", birth_city: "", death_city: "",
  birth_date: "", death_date: "", socials: { linkedin: "", instagram: "", tiktok: "" },
};
const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };
const cls = "w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]";

export default function Lineage() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [role, setRole] = useState("user");
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [usedUserIds, setUsedUserIds] = useState(new Set());

  const [sheetOpen, setSheetOpen] = useState(false);
  const [step, setStep] = useState("choose"); // choose | active | deceased | view
  const [editing, setEditing] = useState(null); // node object being edited/viewed
  const [dform, setDform] = useState(emptyDeceased);
  const [selectedMember, setSelectedMember] = useState("");
  const [saving, setSaving] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [dir, setDir] = useState({ living: [], ancestors: [] });
  const [listTab, setListTab] = useState("living");
  const navigate = useNavigate();

  const canEdit = role === "admin" || role === "organizer";

  const openNode = useCallback((raw) => {
    setEditing(raw);
    if (raw.deceased) {
      setDform({
        name: raw.name || "", photo: raw.photo || "", occupation: raw.occupation || "", bio: raw.bio || "",
        birth_city: raw.birth_city || "", death_city: raw.death_city || "",
        birth_date: raw.birth_date || "", death_date: raw.death_date || "", hobbies: raw.hobbies || "",
        socials: { linkedin: raw.socials?.linkedin || "", instagram: raw.socials?.instagram || "", tiktok: raw.socials?.tiktok || "" },
      });
    }
    setStep("view");
    setSheetOpen(true);
  }, []);

  // Clicking a card opens the full external profile (same as the list menu).
  const handleOpenCard = useCallback((raw) => {
    if (raw.deceased) navigate(`/a/${raw.id}`);
    else if (raw.user_id) navigate(`/u/${raw.user_id}`);
  }, [navigate]);

  // Admin edit of an ancestor card (opened via ?edit= from the profile page).
  const openEditNode = useCallback((raw) => {
    setEditing(raw);
    setDform({
      name: raw.name || "", photo: raw.photo || "", occupation: raw.occupation || "", bio: raw.bio || "",
      birth_city: raw.birth_city || "", death_city: raw.death_city || "",
      birth_date: raw.birth_date || "", death_date: raw.death_date || "", hobbies: raw.hobbies || "",
      socials: { linkedin: raw.socials?.linkedin || "", instagram: raw.socials?.instagram || "", tiktok: raw.socials?.tiktok || "" },
    });
    setStep("deceased");
    setSheetOpen(true);
  }, []);

  const load = useCallback(async () => {
    try {
      const [{ data }, membersRes] = await Promise.all([
        api.get("/lineage"),
        api.get("/families/members").catch(() => ({ data: [] })),
      ]);
      setRole(data.role);
      setMembers(membersRes.data);
      setUsedUserIds(new Set(data.nodes.filter((n) => n.user_id).map((n) => n.user_id)));
      // Auto-layout by generation so cards never overlap and are easy to tap.
      const layout = (() => {
        const parentEdges = data.edges.filter((e) => e.relation !== "spouse");
        const childrenOf = {}; const hasParent = {};
        parentEdges.forEach((e) => { (childrenOf[e.source] = childrenOf[e.source] || []).push(e.target); hasParent[e.target] = true; });
        const depth = {};
        const assign = (id, d, seen) => {
          if (seen.has(id)) return; seen.add(id);
          depth[id] = Math.max(depth[id] ?? 0, d);
          (childrenOf[id] || []).forEach((c) => assign(c, d + 1, seen));
        };
        data.nodes.forEach((n) => { if (!hasParent[n.id]) assign(n.id, 0, new Set()); });
        data.nodes.forEach((n) => { if (depth[n.id] == null) depth[n.id] = 0; });
        const rows = {};
        data.nodes.forEach((n) => { (rows[depth[n.id]] = rows[depth[n.id]] || []).push(n); });
        const GX = 260; const GY = 240; const pos = {};
        Object.keys(rows).forEach((d) => {
          const row = rows[d];
          row.forEach((n, i) => { pos[n.id] = { x: (i - (row.length - 1) / 2) * GX, y: Number(d) * GY }; });
        });
        return pos;
      })();
      const rfNodes = data.nodes.map((n) => ({
        id: n.id, type: "member", position: layout[n.id] || { x: n.x || 0, y: n.y || 0 },
        data: { ...n, onOpen: () => handleOpenCard(n) },
      }));
      const rfEdges = data.edges.map((e) => ({
        id: e.id, source: e.source, target: e.target, animated: e.relation === "spouse",
        style: e.relation === "spouse" ? { stroke: "#8AB4F8", strokeDasharray: "5 5" } : {},
      }));
      setNodes(rfNodes);
      setEdges(rfEdges);
      // Admin arrived from an ancestor profile's Edit button -> open the edit form.
      const editId = new URLSearchParams(window.location.search).get("edit");
      if (editId) {
        const raw = data.nodes.find((n) => n.id === editId);
        if (raw && raw.deceased) openEditNode(raw);
        window.history.replaceState({}, "", "/lineage");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [handleOpenCard, openEditNode, setNodes, setEdges]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.body.classList.add("lineage-lock");
    return () => document.body.classList.remove("lineage-lock");
  }, []);

  const openList = async () => {
    setListOpen(true);
    try { const { data } = await api.get("/directory"); setDir(data); } catch {}
  };

  const onConnect = useCallback(async (conn) => {
    if (!canEdit) return;
    try {
      const { data } = await api.post("/lineage/edges", { source: conn.source, target: conn.target, relation: "parent" });
      setEdges((eds) => addEdge({ id: data.id, source: conn.source, target: conn.target }, eds));
      toast.success("Connected");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [canEdit, setEdges]);

  const onNodeDragStop = useCallback(async (_, node) => {
    if (!canEdit) return;
    try { await api.patch(`/lineage/nodes/${node.id}/position`, { x: node.position.x, y: node.position.y }); } catch {}
  }, [canEdit]);

  const openNew = () => {
    setEditing(null);
    setDform(emptyDeceased);
    setSelectedMember("");
    setStep("choose");
    setSheetOpen(true);
  };

  const availableMembers = useMemo(
    () => members.filter((m) => !usedUserIds.has(m.user_id)),
    [members, usedUserIds]
  );

  const saveActive = async () => {
    if (!selectedMember) { toast.error("Select a member"); return; }
    setSaving(true);
    try {
      await api.post("/lineage/nodes", { user_id: selectedMember, x: Math.random() * 250, y: Math.random() * 250 });
      toast.success("Member added to tree");
      setSheetOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveDeceased = async () => {
    if (!dform.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/lineage/nodes/${editing.id}`, dform);
        toast.success("Card updated");
      } else {
        await api.post("/lineage/nodes", { deceased: true, ...dform, x: Math.random() * 250, y: Math.random() * 250 });
        toast.success("Card added");
      }
      setSheetOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing) return;
    try {
      await api.delete(`/lineage/nodes/${editing.id}`);
      toast.success("Removed from tree");
      setSheetOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const df = (k, ph, type = "text") => (
    <input value={dform[k]} onChange={(e) => setDform({ ...dform, [k]: e.target.value })}
      placeholder={ph} type={type} data-testid={`deceased-field-${k}`}
      className={`${cls} min-w-0`} style={inputStyle} />
  );
  const social = (key, Icon, ph) => (
    <div className="flex items-center gap-2 rounded-xl px-3" style={inputStyle}>
      <Icon size={15} style={{ color: "var(--text-muted)" }} />
      <input value={dform.socials[key]} onChange={(e) => setDform({ ...dform, socials: { ...dform.socials, [key]: e.target.value } })}
        placeholder={ph} data-testid={`deceased-social-${key}`}
        className="flex-1 bg-transparent py-3 text-sm outline-none" style={{ color: "var(--text-primary)" }} />
    </div>
  );

  const ageOf = (n) => {
    if (!n?.birth_date) return null;
    const b = new Date(n.birth_date);
    if (isNaN(b)) return null;
    const end = n.death_date ? new Date(n.death_date) : new Date();
    let a = end.getFullYear() - b.getFullYear();
    const m = end.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && end.getDate() < b.getDate())) a--;
    return a >= 0 ? a : null;
  };

  const sheetTitle = step === "choose" ? "Add to Tree"
    : step === "active" ? "Add a Member"
    : step === "deceased" ? (editing ? "Edit Card" : "In Memoriam")
    : "";

  return (
    <div>
      <AppHeader title="Lineage" />
      <div style={{ height: "calc(100dvh - 210px)" }} className="relative">
        {loading ? (
          <div className="grid place-items-center h-full"><Loader2 className="animate-spin gold-text" /></div>
        ) : nodes.length === 0 ? (
          <div className="grid place-items-center h-full px-10 text-center">
            <div>
              <div className="font-serif-display text-2xl">Your family tree awaits</div>
              <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
                {canEdit ? "Tap + to add a living member or an ancestor." : "An admin hasn't built the tree yet."}
              </p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onConnect={onConnect} onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes} fitView fitViewOptions={{ padding: 0.4, maxZoom: 1 }} minZoom={0.2} maxZoom={1.5}
            nodesDraggable={canEdit} nodesConnectable={canEdit} elementsSelectable={canEdit}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={26} size={1.5} color="var(--overlay)" />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}

        <motion.button whileTap={{ scale: 0.92 }} onClick={openList}
          data-testid="list-view-btn"
          className="absolute top-3 right-3 h-10 rounded-full px-4 grid place-items-center z-10 card-surface"
          style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.18)" }}>
          <span className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--text-primary)" }}>
            <List size={16} className="gold-text" /> List
          </span>
        </motion.button>

        {canEdit && (
          <motion.button whileTap={{ scale: 0.9 }} onClick={openNew}
            data-testid="add-member-fab"
            className="absolute bottom-5 right-5 h-14 w-14 rounded-full grid place-items-center z-10"
            style={{ background: "var(--accent)", color: "#fff", boxShadow: "0 8px 24px rgba(124,131,255,0.4)" }}>
            <Plus size={26} />
          </motion.button>
        )}
      </div>

      <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={sheetTitle}>
        {step === "choose" && (
          <div>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
              Every card links to a living family member or an ancestor who has passed.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => setStep("active")}
                data-testid="add-active-btn" className="card-surface rounded-2xl p-5 flex flex-col items-center gap-2">
                <UserCheck size={26} className="gold-text" />
                <span className="text-sm font-medium">Living member</span>
                <span className="text-[11px] text-center" style={{ color: "var(--text-muted)" }}>Link an app account</span>
              </motion.button>
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => setStep("deceased")}
                data-testid="add-deceased-btn" className="card-surface rounded-2xl p-5 flex flex-col items-center gap-2">
                <Cross size={24} className="gold-text" />
                <span className="text-sm font-medium">Ancestor</span>
                <span className="text-[11px] text-center" style={{ color: "var(--text-muted)" }}>Someone deceased</span>
              </motion.button>
            </div>
          </div>
        )}

        {step === "active" && (
          <div className="space-y-2">
            <p className="text-sm mb-2" style={{ color: "var(--text-secondary)" }}>Select an app member to add.</p>
            {availableMembers.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>All members already have a card.</p>
            ) : availableMembers.map((m) => (
              <button key={m.user_id} onClick={() => setSelectedMember(m.user_id)}
                data-testid={`member-choice-${m.user_id}`}
                className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors"
                style={{ background: selectedMember === m.user_id ? "rgba(124,131,255,0.14)" : "var(--overlay)", border: selectedMember === m.user_id ? "1px solid rgba(124,131,255,0.4)" : "1px solid transparent" }}>
                <Avatar name={m.name} sm />
                <div className="text-left"><div className="text-sm font-medium">{m.name}</div>
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{m.email}</div></div>
              </button>
            ))}
            <motion.button whileTap={{ scale: 0.97 }} onClick={saveActive} disabled={saving || !selectedMember}
              data-testid="save-active-btn"
              className="w-full mt-3 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: "var(--accent)", color: "var(--bg-base)", opacity: selectedMember ? 1 : 0.5 }}>
              {saving && <Loader2 size={16} className="animate-spin" />} Add to Tree
            </motion.button>
            <button onClick={() => setStep("choose")} className="w-full text-sm py-2" style={{ color: "var(--text-muted)" }}>← Back</button>
          </div>
        )}

        {step === "deceased" && (
          <div className="space-y-3">
            <div className="flex justify-center">
              <div className="h-24 w-24 rounded-full overflow-hidden gold-border grid place-items-center" style={{ background: "var(--bg-elevated)" }}>
                {dform.photo ? <img src={fileUrl(dform.photo)} alt="" className="h-full w-full object-cover" />
                  : <span className="font-serif-display text-3xl gold-text">{(dform.name || "?").charAt(0)}</span>}
              </div>
            </div>
            <MediaUploader label="Upload portrait" testid="deceased-photo-upload" onUploaded={(p) => setDform((f) => ({ ...f, photo: p }))} />
            {df("name", "Full name")}
            {df("occupation", "Occupation")}
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Born</label>
                {df("birth_date", "", "date")}
              </div>
              <div className="min-w-0">
                <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Died</label>
                {df("death_date", "", "date")}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Birth city</label>
                {df("birth_city", "City")}
              </div>
              <div className="min-w-0">
                <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>City of passing</label>
                {df("death_city", "City")}
              </div>
            </div>
            <textarea value={dform.bio} onChange={(e) => setDform({ ...dform, bio: e.target.value })}
              placeholder="Biography…" rows={3} data-testid="deceased-field-bio" className={cls} style={inputStyle} />
            {df("hobbies", "Hobbies & interests")}
            <div className="text-[11px] uppercase tracking-widest pt-1" style={{ color: "var(--text-muted)" }}>Social links (optional)</div>
            {social("linkedin", Linkedin, "LinkedIn")}
            {social("instagram", Instagram, "Instagram")}
            {social("tiktok", Music2, "TikTok")}
            <motion.button whileTap={{ scale: 0.97 }} onClick={saveDeceased} disabled={saving}
              data-testid="save-deceased-btn"
              className="w-full rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              {saving && <Loader2 size={16} className="animate-spin" />} {editing ? "Save Changes" : "Add Card"}
            </motion.button>
            {editing ? (
              <button onClick={remove} data-testid="delete-member-btn" className="w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2" style={{ color: "var(--destructive)" }}>
                <Trash2 size={15} /> Remove from tree
              </button>
            ) : (
              <button onClick={() => setStep("choose")} className="w-full text-sm py-2" style={{ color: "var(--text-muted)" }}>← Back</button>
            )}
          </div>
        )}

        {step === "view" && editing && (
          <div className="pb-2 relative">
            {canEdit && editing.deceased && (
              <button onClick={() => setStep("deceased")} data-testid="edit-ancestor-btn"
                className="absolute top-0 left-0 h-9 w-9 rounded-full grid place-items-center"
                style={{ background: "var(--overlay)" }}>
                <Pencil size={15} className="gold-text" />
              </button>
            )}
            <div className="text-center">
              <div className="h-24 w-24 mx-auto rounded-full overflow-hidden gold-border grid place-items-center" style={{ background: "var(--bg-elevated)" }}>
                {editing.photo ? <img src={fileUrl(editing.photo)} alt="" className="h-full w-full object-cover" />
                  : <span className="font-serif-display text-3xl gold-text">{(editing.name || "?").charAt(0)}</span>}
              </div>
              <div className="font-serif-display text-3xl mt-3">{editing.name}</div>
              {(() => {
                const age = ageOf(editing);
                const line = [editing.occupation, age != null ? String(age) : null].filter(Boolean).join(", ");
                return line ? <div className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>{line}</div> : null;
              })()}
              {(editing.birth_city || editing.location) && <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{editing.birth_city || editing.location}</div>}
              {editing.deceased && (
                <span className="inline-block mt-2 text-[10px] uppercase tracking-widest rounded-full px-2 py-1"
                  style={{ background: "var(--overlay)", color: "var(--accent)" }}>
                  In Memoriam
                </span>
              )}
            </div>
            {editing.bio && <p className="text-sm mt-4" style={{ color: "var(--text-secondary)" }}>{editing.bio}</p>}
            {editing.hobbies && (
              <div className="flex items-center gap-2 mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                <Sparkles size={14} className="gold-text" /> {editing.hobbies}
              </div>
            )}
            {canEdit && (
              <button onClick={remove} data-testid="delete-member-btn" className="w-full mt-4 rounded-xl py-3 text-sm flex items-center justify-center gap-2" style={{ color: "var(--destructive)" }}>
                <Trash2 size={15} /> Remove from tree
              </button>
            )}
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={listOpen} onClose={() => setListOpen(false)} title="Family Directory">
        <div className="flex gap-2 mb-4">
          {[["living", "Living members"], ["ancestors", "Ancestors"]].map(([k, label]) => (
            <button key={k} onClick={() => setListTab(k)} data-testid={`list-tab-${k}`}
              className="rounded-full px-4 py-2 text-xs font-medium"
              style={listTab === k
                ? { background: "var(--accent)", color: "#fff" }
                : { background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
              {label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {(listTab === "living" ? dir.living : dir.ancestors).length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              {listTab === "living" ? "No members yet." : "No ancestors added yet."}
            </p>
          ) : (listTab === "living" ? dir.living : dir.ancestors).map((e) => (
            <button key={e.id} data-testid={`directory-entry-${e.id}`}
              onClick={() => { setListOpen(false); navigate(listTab === "living" ? `/u/${e.id}` : `/a/${e.id}`); }}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left" style={{ background: "var(--overlay)" }}>
              <div className="h-10 w-10 rounded-full overflow-hidden gold-border grid place-items-center shrink-0" style={{ background: "var(--bg-elevated)" }}>
                {e.photo ? <img src={fileUrl(e.photo)} alt="" className="h-full w-full object-cover" />
                  : <span className="font-serif-display gold-text">{(e.name || "?").charAt(0)}</span>}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{e.name}</div>
                <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>
                  {listTab === "living"
                    ? (e.occupation || e.role)
                    : ([e.birth_date, e.death_date].filter(Boolean).map((d) => (d || "").slice(0, 4)).join(" – ") || "In Memoriam")}
                </div>
              </div>
            </button>
          ))}
        </div>
      </BottomSheet>
    </div>
  );
}
